import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import type { Benefit } from '../types';

const OPENED_BENEFIT_MODAL = 'openedBenefitModal';

export interface UseBenefitModalRouteReturn {
  selectedBenefit: Benefit | null;
  isModalOpen: boolean;
  openBenefit: (benefit: Benefit) => void;
  closeModal: () => void;
}

/**
 * Syncs the "selected benefit" detail modal with the `benefitId` URL param,
 * so the browser back button closes it
 */
export const useBenefitModalRoute = (
  benefits: Benefit[] | undefined,
): UseBenefitModalRouteReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const benefitIdFromUrl = searchParams.get('benefitId');

  const selectedBenefit = useMemo(() => {
    if (!benefitIdFromUrl || !benefits) return null;
    return benefits.find((benefit) => benefit.id === benefitIdFromUrl) ?? null;
  }, [benefitIdFromUrl, benefits]);

  const openBenefit = (benefit: Benefit) => {
    const params = new URLSearchParams(searchParams);
    params.set('benefitId', benefit.id);
    setSearchParams(params, {
      state: { ...(location.state ?? {}), [OPENED_BENEFIT_MODAL]: true },
    });
  };

  const closeModal = () => {
    // Opened from the list: pop the entry we pushed so back leaves the list.
    if (location.state?.[OPENED_BENEFIT_MODAL]) {
      navigate(-1);
      return;
    }
    // Deep-linked: there is no entry of ours to pop, so rewrite the current one.
    const params = new URLSearchParams(searchParams);
    params.delete('benefitId');
    setSearchParams(params, { replace: true });
  };

  return {
    selectedBenefit,
    isModalOpen: !!selectedBenefit,
    openBenefit,
    closeModal,
  };
};
